import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Activity, AlertOctagon, FileText, Network, Lock, Terminal, ArrowRight, Check } from 'lucide-react';
import { APP } from '@/constants/testIds';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const HERO_BG = 'https://images.pexels.com/photos/30175347/pexels-photo-30175347.jpeg';
const SERVER_BG = 'https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg';

const PackageCard = ({ id, badge, name, price, sub, items, ctaTestId, highlight, onChoose }) => (
  <div
    data-testid={id}
    className={`panel p-8 flex flex-col h-full relative ${highlight ? 'border-[color:var(--brand)]' : ''}`}
  >
    {highlight && (
      <span className="absolute -top-3 left-6 pill pill-brand">RECOMMENDED</span>
    )}
    <div className="label-tag mb-3">{badge}</div>
    <div className="text-2xl font-light mb-1 tracking-tight">{name}</div>
    <div className="flex items-end gap-2 mb-6">
      <span className="text-5xl font-light tracking-tight">${price}</span>
      <span className="text-[color:var(--text-muted)] font-mono text-xs uppercase tracking-widest pb-2">{sub}</span>
    </div>
    <ul className="space-y-3 mb-8 flex-1">
      {items.map((it) => (
        <li key={it} className="flex items-start gap-3 text-sm text-[color:var(--text-secondary)]">
          <Check size={14} className="mt-1 text-[color:var(--brand)] flex-shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
    <button data-testid={ctaTestId} onClick={onChoose} className={highlight ? 'btn-primary justify-center' : 'btn-secondary justify-center'}>
      Request Package <ArrowRight size={14} />
    </button>
  </div>
);

const Landing = () => {
  const [contact, setContact] = useState({ name: '', email: '', company: '', package: 'plus', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const goLogin = () => { window.location.href = '/login'; };
  const choosePkg = (pkg) => {
    setContact((c) => ({ ...c, package: pkg }));
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contact', contact);
      setSubmitted(true);
      toast.success('Request received. Our security team will reach out.');
    } catch (_e) {
      toast.error('Could not submit. Try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)] text-white">
      {/* Top bar */}
      <header className="border-b border-[color:var(--border)] sticky top-0 z-30 backdrop-blur bg-[color:var(--bg-base)]/85">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 h-14 md:h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <span className="logo-mark shrink-0">S</span>
            <span className="font-mono text-[10px] sm:text-xs tracking-[0.22em] sm:tracking-[0.28em] uppercase truncate">Supreme<span className="text-[color:var(--brand)]">/</span>Web Security</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 font-mono text-xs tracking-[0.22em] uppercase text-[color:var(--text-secondary)]">
            <a href="#packages" className="hover:text-white">Packages</a>
            <a href="#how" className="hover:text-white">How it works</a>
            <a href="#contact" className="hover:text-white">Contact</a>
          </nav>
          <button data-testid={APP.landingGetStarted} onClick={goLogin} className="btn-primary !px-4 !py-2 sm:!px-7 sm:!py-3.5 shrink-0">
            <span>Sign In</span> <ArrowRight size={14} />
          </button>
        </div>
      </header>

      {/* Hero */}
      <section data-testid={APP.landingHero} className="relative overflow-hidden border-b border-[color:var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'grayscale(0.4)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--bg-base)]/70 via-transparent to-[color:var(--bg-base)]" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="pill pill-brand"><span className="w-1.5 h-1.5 bg-[color:var(--brand)] rounded-full blink" /> Live Network Monitoring</span>
              <span className="label-tag">v1.0 // Tactical</span>
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-[88px] leading-[1.02] tracking-tight font-light mb-6">
              Know Every Device.<br />
              <span className="text-[color:var(--brand)]">Track Every IP.</span><br />
              Secure Every Network.
            </h1>
            <p className="text-[color:var(--text-secondary)] max-w-2xl text-base md:text-lg leading-relaxed mb-10">
              Supreme Web Security IP Tracker maps every device on your home or business network, surfaces unknown IPs, flags suspicious traffic, and delivers forensic-grade reports — built for homeowners, businesses, and Web PI investigators.
            </p>
            <div className="flex flex-wrap gap-3">
              <button data-testid={APP.landingStartScan} onClick={goLogin} className="btn-primary">
                <Activity size={14} /> Start Security Scan
              </button>
              <button data-testid={APP.landingViewReport} onClick={goLogin} className="btn-secondary">
                <FileText size={14} /> View My Report
              </button>
              <button data-testid={APP.landingRequestPI} onClick={() => choosePkg('web_pi')} className="btn-secondary">
                <Terminal size={14} /> Request Web PI Review
              </button>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="panel p-6 relative">
              <div className="absolute -top-3 left-6 pill pill-muted">// LIVE/MOCK SCAN PREVIEW</div>
              <div className="space-y-2 font-mono text-[12px] text-[color:var(--text-secondary)]">
                <div className="flex justify-between"><span>192.168.1.1</span><span className="pill pill-low">KNOWN</span></div>
                <div className="flex justify-between"><span>192.168.1.34</span><span className="pill pill-medium">NEW</span></div>
                <div className="flex justify-between"><span>192.168.1.88</span><span className="pill pill-critical">SUSPICIOUS</span></div>
                <div className="flex justify-between"><span>192.168.1.119</span><span className="pill pill-low">KNOWN</span></div>
                <div className="flex justify-between"><span>192.168.1.201</span><span className="pill pill-high">OLD</span></div>
              </div>
              <div className="glow-divider my-5" />
              <div className="grid grid-cols-3 gap-4">
                <div><div className="label-tag">Devices</div><div className="font-mono text-2xl">14</div></div>
                <div><div className="label-tag">Alerts</div><div className="font-mono text-2xl text-[color:var(--status-high)]">3</div></div>
                <div><div className="label-tag">Risk</div><div className="font-mono text-2xl text-[color:var(--status-medium)]">MED</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features strip */}
      <section id="how" className="border-b border-[color:var(--border)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-20 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--border)]">
          {[
            { i: <Network size={20} />, t: 'Network Discovery', d: 'Identify every active device — IP, MAC, vendor, connection type.' },
            { i: <AlertOctagon size={20} />, t: 'Threat Flagging', d: 'Flag unknown, suspicious, or unauthorized hosts in real time.' },
            { i: <Activity size={20} />, t: 'History & Tracking', d: 'Save scans by date. Track first seen / last seen on every device.' },
            { i: <FileText size={20} />, t: 'Forensic Reports', d: 'PDF + CSV exports with optional email delivery.' },
          ].map((f) => (
            <div key={f.t} className="bg-[color:var(--bg-base)] p-8">
              <div className="text-[color:var(--brand)] mb-4">{f.i}</div>
              <div className="label-tag mb-2">Module</div>
              <div className="text-xl mb-3 font-light">{f.t}</div>
              <div className="text-sm text-[color:var(--text-secondary)] leading-relaxed">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="relative border-b border-[color:var(--border)]">
        <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-20 hidden lg:block"
             style={{ backgroundImage: `url(${SERVER_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0 bg-[color:var(--bg-base)]/90" />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-4">
              <div className="label-tag mb-3">// Business Packages</div>
              <h2 className="text-4xl md:text-5xl font-light tracking-tight">Pick a tier. We do the rest.</h2>
            </div>
            <p className="md:col-span-7 md:col-start-6 text-[color:var(--text-secondary)] text-base leading-relaxed self-end">
              Every plan ships with consent-gated scanning, secure customer portal access, and our forensic IP tracker engine. Upgrade for monthly monitoring, executive reports, and Web PI investigations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[color:var(--border)]">
            <PackageCard
              id={APP.packageScan}
              ctaTestId={APP.packageScanCta}
              badge="Package 01 // One-Time"
              name="Business Security Scan"
              price="999"
              sub="USD / one-time"
              items={[
                'One-time network scan',
                'IP / device inventory report',
                'Unknown device detection',
                'Old IP address review',
                'Basic risk summary',
                'PDF security report',
              ]}
              onChoose={() => choosePkg('scan')}
            />
            <PackageCard
              id={APP.packagePlus}
              ctaTestId={APP.packagePlusCta}
              badge="Package 02 // Monthly"
              name="Business Protection Plus"
              price="1,499"
              sub="USD / month"
              highlight
              items={[
                'Everything in $999 package',
                'Monthly automated scans',
                'Customer portal access',
                'Email security alerts',
                'Admin review notes',
                'Device trust list',
                'Suspicious IP flagging',
              ]}
              onChoose={() => choosePkg('plus')}
            />
            <PackageCard
              id={APP.packageWebPI}
              ctaTestId={APP.packageWebPiCta}
              badge="Package 03 // Web PI"
              name="Business Web PI Package"
              price="2,500"
              sub="USD / month"
              items={[
                'Everything in Plus',
                'Advanced forensic IP tracking',
                'Multiple location support',
                'Technician dashboard review',
                'Priority security alerts',
                'Monthly executive report',
                'Failed login attempt tracking',
                'External access monitoring',
                'Business compliance summary',
              ]}
              onChoose={() => choosePkg('web_pi')}
            />
          </div>
        </div>
      </section>

      {/* Security notice */}
      <section className="border-b border-[color:var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 flex items-start gap-6">
          <Lock className="text-[color:var(--brand)] mt-1" size={28} />
          <div>
            <div className="label-tag mb-2">// Security Permission</div>
            <p className="text-lg max-w-3xl leading-relaxed">
              Before scanning, every user must confirm: <span className="text-[color:var(--brand)]">“I confirm that I own or have authorization to scan this network and request Supreme Web Security to perform a security review.”</span>
            </p>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="border-b border-[color:var(--border)]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-16 sm:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-5">
            <div className="label-tag mb-3">// Contact Security Team</div>
            <h2 className="text-4xl font-light tracking-tight mb-6">Talk to a security analyst.</h2>
            <p className="text-[color:var(--text-secondary)] leading-relaxed mb-6">
              Tell us about your network footprint, locations, and current concerns. We respond within one business day with a tailored scope and quote.
            </p>
            <div className="font-mono text-xs tracking-widest uppercase text-[color:var(--text-secondary)] space-y-2">
              <div>Email · security@supremeweb.example</div>
              <div>Hours · 24/7 incident response</div>
              <div>Locations · USA · EU</div>
            </div>
          </div>
          <form data-testid={APP.contactForm} onSubmit={submitContact} className="md:col-span-7 panel p-8 space-y-5">
            {submitted ? (
              <div className="py-16 text-center">
                <ShieldCheck className="mx-auto text-[color:var(--brand)] mb-4" size={36} />
                <div className="text-2xl font-light mb-2">Request received.</div>
                <p className="text-[color:var(--text-secondary)] text-sm font-mono">Our security team will reach out within one business day.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <div className="label-tag mb-2">Full Name</div>
                    <input required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="input-base" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <div className="label-tag mb-2">Email</div>
                    <input required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="input-base" data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <div className="label-tag mb-2">Company</div>
                    <input value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} className="input-base" data-testid="contact-company-input" />
                  </div>
                  <div>
                    <div className="label-tag mb-2">Package</div>
                    <select value={contact.package} onChange={(e) => setContact({ ...contact, package: e.target.value })} className="input-base" data-testid="contact-package-select">
                      <option value="scan">Business Security Scan — $999</option>
                      <option value="plus">Protection Plus — $1,499</option>
                      <option value="web_pi">Web PI Package — $2,500</option>
                    </select>
                  </div>
                </div>
                <div>
                  <div className="label-tag mb-2">Message</div>
                  <textarea rows={4} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className="input-base" data-testid="contact-message-input" />
                </div>
                <button type="submit" data-testid={APP.contactSubmit} className="btn-primary">
                  <ArrowRight size={14} /> Contact Security Team
                </button>
              </>
            )}
          </form>
        </div>
      </section>

      <footer className="border-t border-[color:var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="logo-mark">S</span>
            <span className="font-mono text-xs tracking-[0.28em] uppercase">Supreme Web Security IP Tracker</span>
          </div>
          <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--text-muted)]">
            © {new Date().getFullYear()} · Know every device. Track every IP. Secure every network.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
